import axios from 'axios';

const API_URL = `${import.meta.env.VITE_API_URL}/api`;

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Globálny error handler — zobrazí toast pre bežné chyby
// showToast sa nastavuje z ToastContext po mountnutí appky
export let showGlobalToast: ((msg: string, type?: 'error' | 'success' | 'info') => void) | null = null;
export function setGlobalToast(fn: typeof showGlobalToast) {
  showGlobalToast = fn;
}

// Priloží JWT token ku každému requestu
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  res => res,
  err => {
    const status = err.response?.status;
    const msg = err.response?.data?.error;

    // Zablokovaný účet — vyhodiť zo session a presmerovať
    if (status === 403 && msg === 'ACCOUNT_BANNED') {
      const banReason = err.response?.data?.ban_reason || '';
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/account-banned') {
        sessionStorage.setItem('ban_reason', banReason);
        window.location.replace('/account-banned');
      }
      return Promise.reject(err);
    }

    // Expirovaný/neplatný token — auto-logout a redirect na login
    // Verejné stránky (login, register, reset) ignorujeme, nech sa neceyklí
    if (status === 401) {
      const path = window.location.pathname;
      const publicPaths = ['/', '/login', '/register', '/forgot-password', '/reset-password', '/verify-email'];
      const isPublic = publicPaths.includes(path) || path.startsWith('/reset-password/');
      if (!isPublic) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.replace('/login');
      }
      return Promise.reject(err);
    }

    // Silent requesty — nezobrazuj toast (pozadímové volania)
    if ((err.config as any)?.silent) return Promise.reject(err);

    if (showGlobalToast) {
      if (status === 429) {
        showGlobalToast(msg || 'Príliš veľa pokusov. Skús to o chvíľu.', 'error');
      } else if (status === 500) {
        showGlobalToast('Chyba servera. Skús to znova.', 'error');
      } else if (!status) {
        showGlobalToast('Problém s pripojením. Skontroluj internet.', 'error');
      }
    }

    return Promise.reject(err);
  }
);

// Autentifikácia
export const authAPI = {
  register: (data: {
    email: string;
    password: string;
    name: string;
    surname?: string;
    birthdate?: string;
    gender?: string;
    lookingForGender?: string;
    city?: string;
    cityLat?: number;
    cityLng?: number;
    lookingFor?: string[];
    wantsQuestionnaire?: boolean;
    consentTerms?: boolean;
    consentPrivacy?: boolean;
    inviteCode?: string;
  }) => api.post('/auth/register', data),

  validateInvite: (code: string) =>
    api.post('/auth/validate-invite', { code }),

  getBetaStatus: () =>
    api.get('/auth/beta-status'),

  login: (data: { email: string; password: string }) =>
    api.post('/auth/login', data),

  checkEmail: (email: string, silent = false) =>
    api.post<{ exists: boolean; verified?: boolean }>('/auth/check-email', { email }, { silent } as any),

  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),

  resetPassword: (token: string, password: string) =>
    api.post('/auth/reset-password', { token, password }),

  changePassword: (_userId: number, currentPassword: string, newPassword: string) =>
    api.post('/auth/change-password', { currentPassword, newPassword }),

  verifyEmail: (token: string) =>
    api.get('/auth/verify-email', { params: { token } }),

  resendVerification: (email: string) =>
    api.post('/auth/resend-verification', { email }),
};

// Používatelia
export const userAPI = {
  getAll: (viewerId?: number, params?: { gender?: string; min_age?: number; max_age?: number }) =>
    api.get('/users', { params: { ...(viewerId ? { viewer_id: viewerId } : {}), ...params } }),
  getById: (id: number) => api.get(`/users/${id}`),
  saveQuestionnaire: (id: number, data: Record<string, any>) =>
    api.put(`/users/${id}/questionnaire`, data),

  updateById: (id: number, data: {
    name: string;
    surname?: string;
    city?: string;
    cityLat?: number;
    cityLng?: number;
    bio?: string;
    gender?: string;
    birthdate?: string;
    lookingFor?: string[];
    lookingForGender?: string;
  }) => api.put(`/users/${id}`, data),

  getPhotos: (userId: number) => api.get(`/users/${userId}/photos`),

  uploadPhoto: (userId: number, file: File, isPrimary: boolean) => {
    const form = new FormData();
    form.append('photo', file);
    form.append('userId', String(userId));
    form.append('isPrimary', String(isPrimary));
    return api.post('/users/photos', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deletePhoto: (photoId: number) => api.delete(`/users/photos/${photoId}`),

  setPrimaryPhoto: (photoId: number) => api.put(`/users/photos/${photoId}/primary`),

  heartbeat: (userId: number) => api.post(`/users/${userId}/heartbeat`),

  getDiscover: (userId: number, params: {
    gender?: string;
    looking_for_gender?: string;
    min_age?: number;
    max_age?: number;
  }) => api.get(`/users/${userId}/discover`, { params }),

  swipe: (userId: number, targetId: number, action: 'like' | 'pass') =>
    api.post(`/users/${userId}/swipe`, { target_id: targetId, action }),

  getSwipeStatus: (userId: number, targetId: number) =>
    api.get(`/users/${userId}/swipe-status/${targetId}`, { silent: true } as any),

  getLikes: (userId: number) => api.get(`/users/${userId}/likes`),

  toggleIncognito: (userId: number) => api.put(`/users/${userId}/incognito`),

  getPendingWarning: () => api.get('/users/me/warning'),
  acknowledgeWarning: (warningId: number) => api.post(`/users/me/warning/${warningId}/acknowledge`),

  getSettings: (userId: number) => api.get(`/users/${userId}/settings`),
  getConsents: (userId: number) => api.get(`/users/${userId}/consents`),
  updateSettings: (userId: number, data: { is_hidden?: boolean; notifications?: Record<string, boolean> }) =>
    api.patch(`/users/${userId}/settings`, data),
  deleteAccount: (userId: number) => api.delete(`/users/${userId}`),

  // Phase 2
  exportData: (userId: number) => api.get(`/users/${userId}/export`, { responseType: 'blob' }),
  getBlocked: (userId: number) => api.get(`/users/${userId}/blocked`),
  unblock: (userId: number, targetId: number) => api.delete(`/blocks/${userId}/block/${targetId}`),
  getTransactions: (userId: number) => api.get(`/users/${userId}/transactions`),
  cancelSubscription: (userId: number) => api.post(`/users/${userId}/cancel-subscription`),
  resumeSubscription: (userId: number) => api.post(`/users/${userId}/resume-subscription`),
  getMyInvites: (userId: number) => api.get(`/users/${userId}/invites`),
};

export const boostAPI = {
  activate: () => api.post('/boost/activate'),
  getStatus: () => api.get('/boost/status'),
};

export const superLikeAPI = {
  send: (targetId: number) => api.post(`/super-like/${targetId}`),
};

export const passAPI = {
  restore: (force = false) => api.post('/pass/restore', { force }),
};

export const requestAPI = {
  send: (_senderId: number, receiverId: number, message: string) =>
    api.post('/requests/send', { receiver_id: receiverId, message }),
  getAll: (userId: number, sort?: string) =>
    api.get(`/requests/${userId}`, { params: sort ? { sort } : {} }),
  getCount: (userId: number) =>
    api.get(`/requests/${userId}/count`),
  getStatus: (senderId: number, receiverId: number) =>
    api.get(`/requests/status/${senderId}/${receiverId}`, { silent: true } as any),
  accept: (requestId: number) =>
    api.post(`/requests/${requestId}/accept`),
  reject: (requestId: number) =>
    api.post(`/requests/${requestId}/reject`),
};

export const chatAPI = {
  getConversations: (userId: number) => api.get(`/chat/${userId}/conversations`),
  getUnreadCount: (userId: number) => api.get(`/chat/${userId}/unread`),
  // before: ID najstaršej načítanej správy — načíta 40 správ pred ňou (scroll nahor)
  getMessages: (userId: number, targetId: number, before?: number) =>
    api.get(`/chat/${userId}/${targetId}/messages`, { params: before ? { before } : {} }),
  sendMessage: (_senderId: number, receiverId: number, content: string) =>
    api.post('/chat/send', { receiver_id: receiverId, content }),
};

export const blockAPI = {
  unmatch: (userId: number, targetId: number) =>
    api.post(`/blocks/${userId}/unmatch/${targetId}`),
  block: (userId: number, targetId: number) =>
    api.post(`/blocks/${userId}/block/${targetId}`),
  unblock: (userId: number, targetId: number) =>
    api.delete(`/blocks/${userId}/block/${targetId}`),
  report: (userId: number, targetId: number, reason: string, description?: string) =>
    api.post(`/blocks/${userId}/report/${targetId}`, { reason, description }),
};

export const viewAPI = {
  record: (_viewerId: number, viewedId: number) =>
    api.post('/views/record', { viewed_id: viewedId }, { silent: true } as any),
  getViewers: (userId: number) => api.get(`/views/${userId}`),
  getProfileStats: (userId: number) => api.get(`/views/${userId}/stats`),
  getNotifications: (userId: number) => api.get(`/notifications/${userId}`),
  markRead: (userId: number) => api.post(`/notifications/${userId}/read`),
};

// Overenie identity
export const verificationAPI = {
  getStatus: (userId: number) => api.get(`/verification/${userId}/status`),
  getChallenge: (userId: number) => api.get(`/verification/${userId}/challenge`),
  submitSelfie: (userId: number, file: File) => {
    const form = new FormData();
    form.append('selfie', file);
    return api.post(`/verification/${userId}/submit`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const featuredAPI = {
  getAvailability: (area: string, type: 'basic' | 'premium') =>
    api.get('/featured/availability', { params: { area, type } }),
  book: (date: string, type: 'basic' | 'premium') =>
    api.post('/featured/book', { date, type }),
  getMy: () => api.get('/featured/my'),
  cancel: () => api.delete('/featured/cancel'),
  upgrade: (data: { date: string }) => api.post('/featured/upgrade', data),
};

export const waitlistAPI = {
  join: (data: { name?: string; email: string; city?: string; consent: boolean; website?: string }) =>
    api.post('/waitlist', data),
};

// Admin API — samostatný axios s adminToken
const adminAxios = axios.create({ baseURL: API_URL });
adminAxios.interceptors.request.use(cfg => {
  const token = localStorage.getItem('adminToken');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

export const adminAPI = {
  login: (password: string) => adminAxios.post('/admin/login', { password }),
  getDashboard: () => adminAxios.get('/admin/dashboard'),
  getUsers: (params: { filter?: string; search?: string; page?: number }) =>
    adminAxios.get('/admin/users', { params }),
  getUserDetail: (id: number) => adminAxios.get(`/admin/users/${id}`),
  blockUser: (id: number, note?: string) => adminAxios.post(`/admin/users/${id}/block`, { note }),
  unblockUser: (id: number) => adminAxios.post(`/admin/users/${id}/unblock`),
  verifyUser: (id: number) => adminAxios.post(`/admin/users/${id}/verify`),
  deleteUser: (id: number) => adminAxios.delete(`/admin/users/${id}`),
  getPhotoQueue: () => adminAxios.get('/admin/photos/queue'),
  approvePhoto: (id: number) => adminAxios.post(`/admin/photos/${id}/approve`),
  rejectPhoto: (id: number) => adminAxios.post(`/admin/photos/${id}/reject`),
  getReports: (status?: string) => adminAxios.get('/admin/reports', { params: { status } }),
  resolveReport: (id: number, action: string, note?: string) =>
    adminAxios.post(`/admin/reports/${id}/resolve`, { action, note }),
  getVerifications: (status?: string) => adminAxios.get('/admin/verifications', { params: { status } }),
  approveVerification: (id: number) => adminAxios.post(`/admin/verifications/${id}/approve`),
  rejectVerification: (id: number, note?: string) =>
    adminAxios.post(`/admin/verifications/${id}/reject`, { note }),

  // Plus
  getPlus: (params: { status?: string; search?: string; date_from?: string; date_to?: string; page?: number }) =>
    adminAxios.get('/admin/plus', { params }),
  activatePlus: (data: { user_id: number; days: number; reason: string }) =>
    adminAxios.post('/admin/plus/activate', data),
  extendPlus: (data: { user_id: number; days: number; reason: string }) =>
    adminAxios.post('/admin/plus/extend', data),
  cancelPlus: (data: { user_id: number; reason: string }) =>
    adminAxios.post('/admin/plus/cancel', data),

  // Credits
  getCreditTransactions: (params: { type?: string; user_id?: number; date_from?: string; date_to?: string; page?: number }) =>
    adminAxios.get('/admin/credits', { params }),
  addCredits: (data: { user_id: number; amount: number; reason: string }) =>
    adminAxios.post('/admin/credits/add', data),
  removeCredits: (data: { user_id: number; amount: number; reason: string }) =>
    adminAxios.post('/admin/credits/remove', data),

  // Featured
  getAdminFeatured: (params: { month?: string }) =>
    adminAxios.get('/admin/featured', { params }),
  cancelFeaturedSlot: (data: { slot_id: number; reason: string; refund_credits: boolean }) =>
    adminAxios.post('/admin/featured/cancel', data),
  moveFeaturedSlot: (data: { slot_id: number; new_date: string; reason: string }) =>
    adminAxios.post('/admin/featured/move', data),

  // Finance
  getFinanceStats: () => adminAxios.get('/admin/finance/stats'),
  exportFinance: (params: { date_from?: string; date_to?: string }) =>
    adminAxios.get('/admin/finance/export', { params, responseType: 'blob' }),

  // Notifications
  getRecipientsCount: (params: { target_type: string; target_user_id?: number }) =>
    adminAxios.get('/admin/notifications/recipients-count', { params }),
  getAdminNotifications: (params: { channel?: string; target_type?: string; date_from?: string; date_to?: string; page?: number }) =>
    adminAxios.get('/admin/notifications', { params }),
  sendAdminNotification: (data: { title: string; message: string; target_type: string; target_user_id?: number; channel: string }) =>
    adminAxios.post('/admin/notifications/send', data),

  // Invite kódy (beta)
  getInvites: (params: { page?: number; limit?: number }) =>
    adminAxios.get('/admin/invites', { params }),
  generateInvites: (count: number) =>
    adminAxios.post('/admin/invites/generate', { count }),
  revokeInvite: (id: number) =>
    adminAxios.delete(`/admin/invites/${id}`),

  // Podpora
  getSupportRequests: (params: { status?: string; search?: string; page?: number }) =>
    adminAxios.get('/admin/support', { params }),
  setSupportStatus: (id: number, status: 'new' | 'resolved') =>
    adminAxios.patch(`/admin/support/${id}/status`, { status }),

  // Waitlist (beta)
  getWaitlist: (params: { status?: string; search?: string; page?: number }) =>
    adminAxios.get('/admin/waitlist', { params }),
  markWaitlistInvited: (id: number) =>
    adminAxios.patch(`/admin/waitlist/${id}/invited`),
  deleteWaitlist: (id: number) =>
    adminAxios.delete(`/admin/waitlist/${id}`),
  bulkWaitlist: (ids: number[], action: 'invite' | 'delete') =>
    adminAxios.post('/admin/waitlist/bulk', { ids, action }),
};